import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import Layout from "../../Shared/Layout";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Container } from "@material-ui/core";
import { useBreakpoints } from "../../hooks/useBreakpoints";
import i18n, { Trans } from "../../utils/i18n";
import { SelfGuideCard } from "../../Shared/SelfGuideCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Combobox } from "@headlessui/react";
import { useState } from "react";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getCookieValue } from "../../utils/getCookieValue";
import { Inertia } from "@inertiajs/inertia";
import { useDebounce } from "@uidotdev/usehooks";

export default function SelfRepairGuidesLayout({
    children,
    title,
    showSearch = true,
    backgroundImageUrl,
    stats = [],
    recentGuides = [],
    popularGuides = [],
    showBackButton = false,
    backButtonUrl,
    searchPlaceholder,
    searchProductCategoryId,
}) {
    const { client } = usePage().props;
    const { isSm, isLg, isXl } = useBreakpoints();
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 300);
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [
            "search",
            "selfrepairflow",
            searchProductCategoryId,
            debouncedQuery,
        ],
        enabled: !!debouncedQuery,
        /**
         * By default RQ considers cached data as stale for useQuery / useInfiniteQuery:
         * https://tanstack.com/query/v4/docs/react/guides/important-defaults
         * This means that on every refocus or tab change the da data will be refetched and
         * several conversions will be tracked.
         * That's why we set a staleTime here to prevent this.a will be refetched.
         */
        staleTime: 5 * 1000 * 60, // 5 mins
        queryFn: async () => {
            const res = await fetch(
                route("selfrepair.guides.search", {
                    query: debouncedQuery,
                    productcategory_id: searchProductCategoryId,
                }),
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                        "X-Requested-With": "XMLHttpRequest",
                    },
                }
            );

            const json = await res.json();

            return json;
        },
    });

    const selfRepairFlows =
        isError || isLoading || !data?.data ? [] : data.data;

    const showOptions =
        selfRepairFlows.length > 0 || isLoading || data?.data?.length === 0;

    return (
        <div>
            <div className="w-full p-4 pb-0">
                <header
                    className="rounded-xl relative w-full bg-center bg-cover shadow-md min-h-[320px]"
                    style={{
                        backgroundImage: `url(${backgroundImageUrl ?? (client?.self_repair_banner_image && `${client.self_repair_banner_image}`) ?? '/images/self-repair-banner.jpg'})`,
                    }}
                >
                    <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50 z-0 rounded-xl overflow-hidden" />
                    <div className="absolute top-0 left-0 z-10 w-full h-full p-8 flex flex-col rounded-xl">
                        {showBackButton && backButtonUrl && (
                            <div className="flex items-center gap-x-2 mb-4">
                                <InertiaLink
                                    className="flex items-center no-underline text-white space-x-1"
                                    href={backButtonUrl}
                                >
                                    <ArrowBackIcon />
                                    <span className="text-xl my-0 text-white">
                                        <Trans i18nKey="common.previous" />
                                    </span>
                                </InertiaLink>
                            </div>
                        )}

                        <div className="flex-1 flex justify-center items-center flex-col">
                            {title && <h1 className="text-white">{title}</h1>}

                            {showSearch && (
                                <div className="w-full max-w-screen-xs relative">
                                    <Combobox onChange={(selfRepairFlow) => {
                                            if (!selfRepairFlow) return;
                                            setQuery(
                                                selfRepairFlow.name
                                            );
                                            Inertia.visit(
                                                route(
                                                    "selfrepair.guides.showSelfRepairFlow",
                                                    {
                                                        category:
                                                            selfRepairFlow
                                                                .productcategory
                                                                ?.slug,
                                                        selfrepairflow:
                                                            selfRepairFlow.slug,
                                                    }
                                                )
                                            );
                                        }}
                                        value="" immediate>
                                        {({ open }) => (
                                            <>
                                                <Combobox.Input
                                                    onFocus={() => refetch()}
                                                    placeholder={
                                                        searchPlaceholder ||
                                                        i18n.t(
                                                            "selfRepair.search"
                                                        )
                                                    }
                                                    className={classNames(
                                                        "w-full outline-none px-6 py-4 border-none rounded-3xl",
                                                        {
                                                            "rounded-b-none":
                                                                open &&
                                                                showOptions &&
                                                                !!debouncedQuery,
                                                        }
                                                    )}
                                                    onChange={(event) =>
                                                        setQuery(
                                                            event.target.value
                                                        )
                                                    }
                                                    value={query}
                                                />

                                                {open &&
                                                    showOptions &&
                                                    !!debouncedQuery && (
                                                        <Combobox.Options
                                                            static
                                                            className="shadow-lg divide-y divide-gray-200 absolute bg-white w-full m-0 p-0 rounded-b-xl border-t border-gray-300 list-none max-h-[240px] overflow-auto"
                                                        >
                                                            {isLoading ? (
                                                                <Combobox.Option
                                                                    disabled
                                                                    className="px-4 py-3 text-sm hover:bg-gray-100 select-none data-[active]:bg-gray-100 data-[headlessui-state=hovered]:bg-gray-100 data-[selected]:bg-gray-100"
                                                                    value={null}
                                                                >
                                                                    <Trans i18nKey="selfRepair.search.loading">
                                                                        Loading
                                                                        ...
                                                                    </Trans>
                                                                </Combobox.Option>
                                                            ) : data?.data
                                                                  ?.length ===
                                                              0 ? (
                                                                <Combobox.Option
                                                                    disabled
                                                                    className="px-4 py-3 text-sm hover:bg-gray-100 select-none data-[active]:bg-gray-100 data-[headlessui-state=hovered]:bg-gray-100 data-[selected]:bg-gray-100"
                                                                    value={null}
                                                                >
                                                                    <Trans i18nKey="selfRepair.search.noResults">
                                                                        No
                                                                        results
                                                                        found
                                                                    </Trans>
                                                                </Combobox.Option>
                                                            ) : (
                                                                <>
                                                                    {selfRepairFlows.map(
                                                                        (
                                                                            selfRepairFlow
                                                                        ) => (
                                                                            <Combobox.Option
                                                                                className="px-4 py-3 text-sm hover:bg-gray-100 select-none data-[active]:bg-gray-100 data-[headlessui-state=hovered]:bg-gray-100 data-[selected]:bg-gray-100"
                                                                                key={
                                                                                    selfRepairFlow.id
                                                                                }
                                                                                value={
                                                                                    selfRepairFlow
                                                                                }
                                                                            >
                                                                                {
                                                                                    selfRepairFlow.name
                                                                                }
                                                                            </Combobox.Option>
                                                                        )
                                                                    )}
                                                                </>
                                                            )}
                                                        </Combobox.Options>
                                                    )}
                                            </>
                                        )}
                                    </Combobox>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
            </div>

            <Container maxWidth="lg" className="py-8">
                {children}
            </Container>

            {stats.length > 0 && (
                <div
                    className="w-full p-8"
                    style={{
                        backgroundColor: client?.brand_color2,
                    }}
                >
                    <div className="flex justify-center w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 w-full">
                            {stats.map((stat) => (
                                <div className="text-center text-white space-y-2">
                                    <div className="text-5xl font-bold">
                                        {stat.count}
                                    </div>
                                    <div>{stat.title}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {(recentGuides.length > 0 || popularGuides.length > 0) && (
                <Container maxWidth="lg" className="py-8">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
                        {recentGuides.length > 0 && (
                            <div className="overflow-hidden">
                                <h2>
                                    <Trans i18nKey="selfRepair.index.latestGuides">
                                        The latest guides
                                    </Trans>
                                </h2>

                                <Swiper
                                    className="w-full max-w-full"
                                    autoplay={true}
                                    autoHeight
                                    loop={recentGuides.length > 1}
                                    modules={[
                                        Navigation,
                                        Pagination,
                                        A11y,
                                        Autoplay,
                                    ]}
                                    spaceBetween={0}
                                    slidesPerView={
                                        isXl ? 1 : isLg ? 3 : isSm ? 2 : 1
                                    }
                                    pagination={{
                                        clickable: true,
                                        el: "#swiper-most-recent-bullet-container",
                                        bulletClass:
                                            "swiper-custom-bullet-rect",
                                        bulletActiveClass:
                                            "swiper-custom-bullet-rect--active",
                                    }}
                                >
                                    {recentGuides.map((guide) => (
                                        <SwiperSlide
                                            className="p-4"
                                            key={guide.id}
                                        >
                                            <InertiaLink
                                                className="no-underline"
                                                href={route(
                                                    "selfrepair.guides.showSelfRepairFlow",
                                                    {
                                                        category:
                                                            guide
                                                                .productcategory
                                                                ?.slug,
                                                        selfrepairflow:
                                                            guide.slug,
                                                    }
                                                )}
                                            >
                                                <SelfGuideCard
                                                    name={guide.name}
                                                    image={guide.image}
                                                    time={guide.time}
                                                    complexity={
                                                        guide.complexity
                                                    }
                                                    targetUser={
                                                        guide.target_user
                                                    }
                                                />
                                            </InertiaLink>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>

                                <div
                                    id="swiper-most-recent-bullet-container"
                                    className="flex items-center justify-center gap-2"
                                />
                            </div>
                        )}

                        {popularGuides.length > 0 && (
                            <div className="overflow-hidden">
                                <h2>
                                    <Trans i18nKey="selfRepair.index.popularGuides">
                                        The most popular guides
                                    </Trans>
                                </h2>

                                <Swiper
                                    className="w-full max-w-full"
                                    autoHeight
                                    loop={popularGuides.length > 1}
                                    modules={[
                                        Navigation,
                                        Pagination,
                                        A11y,
                                        Autoplay,
                                    ]}
                                    autoplay={true}
                                    spaceBetween={0}
                                    slidesPerView={
                                        isXl ? 1 : isLg ? 3 : isSm ? 2 : 1
                                    }
                                    pagination={{
                                        clickable: true,
                                        el: "#swiper-most-liked-bullet-container",
                                        bulletClass:
                                            "swiper-custom-bullet-rect",
                                        bulletActiveClass:
                                            "swiper-custom-bullet-rect--active",
                                    }}
                                >
                                    {popularGuides.map((guide, index) => (
                                        <SwiperSlide
                                            className="p-4"
                                            key={guide.id}
                                        >
                                            <InertiaLink
                                                className="no-underline"
                                                href={route(
                                                    "selfrepair.guides.showSelfRepairFlow",
                                                    {
                                                        category:
                                                            guide
                                                                .productcategory
                                                                ?.slug,
                                                        selfrepairflow:
                                                            guide.slug,
                                                    }
                                                )}
                                            >
                                                <SelfGuideCard
                                                    name={guide.name}
                                                    image={guide.image}
                                                    time={guide.time}
                                                    complexity={
                                                        guide.complexity
                                                    }
                                                    targetUser={
                                                        guide.target_user
                                                    }
                                                />
                                            </InertiaLink>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>

                                <div
                                    id="swiper-most-liked-bullet-container"
                                    className="flex items-center justify-center gap-2"
                                />
                            </div>
                        )}
                    </div>
                </Container>
            )}
        </div>
    );
}

SelfRepairGuidesLayout.layout = (page) => (
    <Layout children={page} wrapperStyle={{ paddingTop: 0 }} />
);
